/**
 * App controller: glues the 3D engine, the plan document (Yjs) and the React UI state.
 * Kept outside React so the engine lives exactly once.
 */
import { openSource, detectWorlds, readLevel, resolveBlockName, ZipReader, type LevelInfo, type SourceInit, type WorldLayout } from "@mcp/world";
import { blockMaterial, DEFAULT_TINT, listBlockMaterials, ModelBaker, type AssetBundle, type BlockMaterial } from "@mcp/assets";
import {
  BUILTIN_TEMPLATES, buildingPoints, counts, defaultParams, looksLikeUrl, makeTemplate, newId, normalizeSourceUrl, parsePlanFile, placeTemplate, polylineLength, PRESETS, runPreset,
  scaledParams, schematicFootprint, schematicFootprintAt, schematicOrigin, turnSchematic,
  type BoardCard, type CardType, type LayoutObject, type PlanFile, type PlanObject, type PresetId, type SchematicRef, type Template, type TemplateObject,
} from "@mcp/plan";
import { embedImages, imageUrl, importImages, prepareImage, unfurl, uploadMissing } from "../lib/images.ts";
import { Engine } from "../engine/Engine.ts";
import { PlanSession, worldIdFor } from "../plan/PlanSession.ts";
import { PlanRenderer, type MaterialInfo } from "../plan/PlanRenderer.ts";
import { PlanTools } from "../plan/PlanTools.ts";
import { StructureRenderer } from "../plan/StructureRenderer.ts";
import { getSchematicFile, listSchematics, MAX_SCHEMATIC_BYTES, nameFromFile, previewDataUrl, removeSchematic, saveSchematic, sha256Hex, updateSchematic, type SchematicEntry } from "../lib/schematics.ts";
import { DEFAULT_SERVER, select, useApp, type AppState, type LayoutState, type RoomState } from "../state/store.ts";
import { openSignIn, updateProfile, useAccount } from "../lib/auth.ts";
import { isPlanLimit, openPlans, refreshBilling } from "../lib/billing.ts";
import {
  acceptInvite, ApiError, claimLegacy, createWorld, deleteWorld, invitePreview, parseAccountInvite, rememberedRoom, rememberRoom, removeMember, roomHeaders, ticket,
  type TicketInfo,
} from "../lib/collab.ts";
import { idb, type CachedFile } from "../lib/idb.ts";
import { isDesktop, readLocalFile, revealPath, saveTextFile, worldFileUrls, worldStamp, type WorldStamp } from "../lib/desktop.ts";
import { clearInviteFromUrl, hostKeyFor, fetchManifest, inviteLink, manifestSource, parseInvite, prebakedMaps, serverOrigin, shareWorld, type RoomHeaders, type WorldManifest } from "../lib/share.ts";
import type { KickReason } from "../plan/PlanSession.ts";
import { MAP_COLOURS_LABEL, mapColoursBundle } from "../lib/mapColours.ts";
import { acceptMojang, downloadClient, findClient, fmtMB } from "../lib/mojang.ts";
import type { RegionMapResult } from "../engine/protocol.ts";
import { DEMO_DIM, DEMO_NAME, DEMO_REGIONS, DEMO_ROOM, DEMO_SKY, DEMO_SPAWN, demoPlan } from "../lib/demoWorld.ts";
import { demoBlocksBundle } from "../lib/demoBlocks.ts";
import type { DemoTopView } from "../engine/demo.worker.ts";
import type { LoadedDemoPack } from "../lib/demoPack.ts";
import { prefersReducedMotion, track, useOnboarding } from "../lib/onboarding.ts";
import { noteOpenedName } from "../lib/crash.ts";

export interface RecentWorld {
  key: string;
  name: string;
  fileName: string;
  size: number;
  savedAt: number;
}

/** an error that has a troubleshooting section in the Academy: the toast links to it (ui/App.tsx) */
function failWith(key: NonNullable<AppState["errorHelp"]>["key"], message: string, extra: Partial<AppState> = {}) {
  useApp.getState().set({ ...extra, error: message, errorHelp: { key, message } });
}

class Controller {
  engine: Engine | null = null;
  session: PlanSession | null = null;
  renderer: PlanRenderer | null = null;
  tools: PlanTools | null = null;
  structures: StructureRenderer | null = null;
  private source: SourceInit | null = null;
  private layout: WorldLayout | null = null;
  private assetsReady = false;
  private bundle: AssetBundle | null = null;
  /** set when the world is streamed from the server (guest); headers = room token of an account world */
  private remote: { server: string; manifest: WorldManifest; headers?: RoomHeaders } | null = null;
  private unobserve: (() => void) | null = null;
  private rebuildTimer = 0;
  /**
   * Which keyboard / clipboard events the planner may act on. null (the app) = all of them. The marketing site's
   * live demo sets it, so keys and copy/paste on the rest of the page stay the page's own (src/embed).
   */
  inputScope: ((e: Event) => boolean) | null = null;

  mount(container: HTMLElement) {
    if (this.engine) {
      container.appendChild(this.engine.renderer.domElement);
      container.appendChild(this.engine.labels.domElement);
      return;
    }
    this.engine = new Engine(container);
    this.engine.onStats = (stats) => useApp.getState().set({ stats });
    this.engine.onMapsChanged = () => {
      clearTimeout(this.rebuildTimer);
      this.rebuildTimer = window.setTimeout(() => {
        this.renderer?.rebuildAll(this.selection());
        // the backdrop's example plan too: its paths and areas lie on the ground the map tells
        this.backdropPlan?.rebuildAll({ ids: [], primary: null });
        this.backdropPlan?.setLabelsVisible(false);
      }, 400);
    };
    const typingTarget = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      return !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
    };
    window.addEventListener("keydown", (e) => {
      if (this.inputScope && !this.inputScope(e)) return;
      const st = useApp.getState();
      if (st.phase === "ready") {
        // 6 = layouts & templates panel
        if ((e.code === "Digit6" || e.code === "Numpad6") && !e.ctrlKey && !e.metaKey && !e.altKey && !typingTarget(e)) { e.preventDefault(); this.openLayouts(st.layoutPanel && st.layoutPanel !== "schematics" ? null : "fill"); return; }
        // 7 = schematics (import & place builds)
        if ((e.code === "Digit7" || e.code === "Numpad7") && !e.ctrlKey && !e.metaKey && !e.altKey && !typingTarget(e)) { e.preventDefault(); this.openLayouts(st.layoutPanel === "schematics" ? null : "schematics"); return; }
        // L = day/night lighting on/off
        if (e.code === "KeyL" && !e.ctrlKey && !e.metaKey && !e.altKey && !typingTarget(e)) { e.preventDefault(); this.setLighting({ on: !st.lighting.on }); return; }
        // Ctrl+S = save plan file (also while typing — the browser's "save page" is never wanted here)
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.code === "KeyS") { e.preventDefault(); this.savePlanFile(); return; }
        // Ctrl+B = fold the plan list into its rail / open it again; Ctrl+F = search the plan
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.code === "KeyB" && !(e.target as HTMLElement | null)?.isContentEditable) { e.preventDefault(); st.set({ sideCollapsed: !st.sideCollapsed }); return; }
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.code === "KeyF" && !st.boardFor) {
          e.preventDefault();
          if (st.sideCollapsed) st.set({ sideCollapsed: false });
          requestAnimationFrame(() => document.querySelector<HTMLInputElement>(".plan-search input")?.focus());
          return;
        }
        // F1 / ? = shortcut overview, Esc closes it
        if (e.key === "F1" || (e.key === "?" && !typingTarget(e))) { e.preventDefault(); st.set({ helpOpen: !st.helpOpen }); return; }
        if (e.key === "Escape" && st.helpOpen) { e.preventDefault(); st.set({ helpOpen: false }); return; }
      }
      // the full-window Schematics view takes its own keys (ui/schematics/SchematicsView.tsx); the map gets none
      if (st.layoutPanel === "schematics") return;
      // keys typed on an open pinboard belong to the board
      if (st.boardFor && (e.target as HTMLElement | null)?.closest?.(".board-panel")) {
        if (e.key === "Escape" && !typingTarget(e)) st.set({ boardFor: null });
        return;
      }
      if (this.tools?.key(e)) { e.preventDefault(); return; }
      if (typingTarget(e) || e.ctrlKey || e.metaKey || e.altKey || useApp.getState().phase !== "ready") return;
      if (this.engine?.setKey(e.code, true)) e.preventDefault();
    });
    window.addEventListener("keyup", (e) => this.engine?.setKey(e.code, false));
    window.addEventListener("blur", () => this.engine?.clearKeys());
    // copy / cut / paste of plan objects via the browser's clipboard events (works across tabs, no permission prompt)
    const typing = () => {
      const a = document.activeElement as HTMLElement | null;
      return !!a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA" || a.isContentEditable);
    };
    const onCopy = (e: ClipboardEvent, cut: boolean) => {
      if (this.inputScope && !this.inputScope(e)) return;
      if (typing() || !this.tools || useApp.getState().phase !== "ready" || useApp.getState().boardFor || useApp.getState().layoutPanel === "schematics") return;
      const text = this.tools.copy();
      if (!text) return;
      e.clipboardData?.setData("text/plain", text);
      e.preventDefault();
      if (cut) this.tools.deleteSelection();
    };
    window.addEventListener("copy", (e) => onCopy(e, false));
    window.addEventListener("cut", (e) => onCopy(e, true));
    window.addEventListener("paste", (e) => {
      if (this.inputScope && !this.inputScope(e)) return;
      // an open pinboard takes pastes itself (images, links, text)
      if (typing() || !this.tools || useApp.getState().phase !== "ready" || useApp.getState().boardFor || useApp.getState().layoutPanel === "schematics") return;
      if (this.tools.paste(e.clipboardData?.getData("text/plain"))) e.preventDefault();
    });
    const pushLighting = () => {
      const l = useApp.getState().lighting;
      this.engine?.setLighting({ on: l.on, ticks: l.time, speed: l.playing ? l.speed : 0 });
    };
    this.engine.onTime = (ticks) => {
      this.timeFromEngine = true;
      useApp.getState().set({ lighting: { ...useApp.getState().lighting, time: ticks } });
      this.timeFromEngine = false;
    };
    pushLighting();
    useApp.subscribe((s, prev) => {
      if (!this.engine) return;
      if (s.lighting !== prev.lighting && !this.timeFromEngine) pushLighting();
      if (s.layout !== prev.layout) this.layoutChanged(s.layout, prev.layout);
      if (s.renderDistance !== prev.renderDistance) this.engine.setSettings({ renderDistance: s.renderDistance });
      // the full-window Schematics view covers the map (and draws its own 3D preview): the map rests meanwhile;
      // leaving the world closes the view (the setup wizard's backdrop needs the engine)
      const sv = (x: AppState) => x.layoutPanel === "schematics" && x.phase === "ready";
      if (sv(s) !== sv(prev)) this.engine.setPaused(sv(s));
      if (s.phase !== "ready" && s.layoutPanel === "schematics") queueMicrotask(() => useApp.getState().set({ layoutPanel: null, schemEdit: null }));
      if (s.cutEnabled !== prev.cutEnabled || s.cutY !== prev.cutY) {
        this.engine.setSettings({ cutY: s.cutEnabled ? s.cutY : null });
        this.structures?.setCut(s.cutEnabled ? s.cutY : null);
      }
      if (s.topDown !== prev.topDown) this.engine.setTopDown(s.topDown);
      if (s.showLabels !== prev.showLabels) this.renderer?.setLabelsVisible(s.showLabels);
      if (s.showLines !== prev.showLines) this.renderer?.setEdgesVisible(s.showLines);
      if (s.selectedIds !== prev.selectedIds || s.selectedId !== prev.selectedId) this.renderer?.sync(s.plans, this.selection());
      if (s.tool !== prev.tool) {
        this.tools?.reset();
        if (s.layout?.mode === "place" && s.tool !== "select") this.cancelLayout();
        if (s.schemPlace && s.tool !== "select") this.cancelSchematicPlacing();
      }
      if (s.dimension !== prev.dimension && this.layout) void this.switchDimension(s.dimension);
    });
    this.engine.setSettings({ renderDistance: useApp.getState().renderDistance });
    void this.refreshSchematics();
  }

  // ---------------- assets ----------------

  /** Called once on startup: detect an #invite=… link (account worlds) or a #join=… link (worlds shared before accounts). */
  async checkInvite(hash?: string, server = DEFAULT_SERVER) {
    const token = parseAccountInvite(hash);
    if (token) {
      void this.previewAccountInvite(token, server);
      return true;
    }
    const inv = parseInvite(hash);
    if (!inv) return false;
    useApp.getState().set({ invite: { ...inv, manifest: null } });
    try {
      const manifest = await fetchManifest(inv.server, inv.room);
      useApp.getState().set({ invite: { ...inv, manifest, error: manifest ? undefined : "The host hasn't shared the world file yet — ask them to click “Share world” in Collaborate." } });
    } catch (e) {
      useApp.getState().set({ invite: { ...inv, manifest: null, error: `Can't reach the host's server (${(e as Error).message}). Is the server online?` } });
    }
    return true;
  }

  /** Desktop: paste an invite link (https://…/#invite=… or #join=…&server=…) instead of opening it in a browser. */
  async joinFromLink(link: string) {
    const i = link.indexOf("#");
    let server = DEFAULT_SERVER;
    try { if (i > 0) server = serverOrigin(link.slice(0, i)); } catch { /* just the hash */ }
    return this.checkInvite(i >= 0 ? link.slice(i) : "#" + link, server);
  }

  // ---------------- shared worlds of accounts (paywall plan P2) ----------------

  /** An #invite=… link: show whose world it is (no sign-in needed yet). */
  async previewAccountInvite(token: string, server = DEFAULT_SERVER) {
    useApp.getState().set({ accountInvite: { token, preview: null, server } });
    try {
      const preview = await invitePreview(server, token);
      useApp.getState().set({ accountInvite: { token, preview, server } });
    } catch (e) {
      useApp.getState().set({ accountInvite: { token, preview: null, server, error: (e as Error).message } });
    }
  }

  /** Join through the open #invite=… link (a free account is needed), then pick jar / map colours like any guest. */
  async acceptAccountInvite() {
    const ai = useApp.getState().accountInvite;
    if (!ai) return;
    if (useAccount.getState().status !== "signed-in") { openSignIn("Joining is free: you only need an account."); return; }
    const server = ai.server;
    useApp.getState().set({ accountInvite: { ...ai, busy: true, error: undefined } });
    try {
      const r = await acceptInvite(server, ai.token);
      clearInviteFromUrl();
      useApp.getState().set({ accountInvite: null });
      await this.prepareSharedWorld(server, r.room_id);
    } catch (e) {
      useApp.getState().set({ accountInvite: { ...ai, busy: false, error: (e as Error).message } });
    }
  }

  /** A shared world this person belongs to → the join screen (world streamed from the server; own jar or map colours). */
  async prepareSharedWorld(server: string, roomId: string) {
    let t: TicketInfo;
    try {
      t = await ticket(server, roomId);
    } catch (e) {
      useApp.getState().set({ error: (e as Error).message });
      return;
    }
    const account = { role: t.role, ownerName: t.world.owner_name, paused: t.paused };
    useApp.getState().set({ invite: { server, room: roomId, manifest: null, account } });
    try {
      const manifest = await fetchManifest(server, roomId, () => roomHeaders(server, roomId));
      useApp.getState().set({ invite: { server, room: roomId, manifest, account, error: manifest ? undefined : `${t.world.owner_name ?? "The owner"} hasn't shared the world files yet. Ask them to click “Share world” in Collaborate.` } });
    } catch (e) {
      useApp.getState().set({ invite: { server, room: roomId, manifest: null, account, error: (e as Error).message } });
    }
  }

  private roomFrom(t: TicketInfo, kind: RoomState["kind"] = "account"): RoomState {
    return { id: t.world.room_id, kind, role: t.role, paused: t.paused, name: t.world.name, ownerName: t.world.owner_name, plan: t.plan };
  }

  /** view only for viewers and in paused worlds (the server drops their edits anyway) */
  private applyRoomAccess() {
    const room = useApp.getState().room;
    const readOnly = !!room && room.kind === "account" && (room.role === "viewer" || room.paused);
    if (this.session) this.session.readOnly = readOnly;
    if (useApp.getState().readOnly !== readOnly) useApp.getState().set({ readOnly });
  }

  /** A fresh ticket for the live connection; also picks up a changed role or a paused world. */
  private async liveTicket(server: string, roomId: string) {
    const t = await ticket(server, roomId);
    const room = useApp.getState().room;
    if (room?.id === roomId) {
      useApp.getState().set({ room: { ...room, role: t.role, paused: t.paused, name: t.world.name, ownerName: t.world.owner_name, plan: t.plan } });
      this.applyRoomAccess();
    }
    return t.ticket;
  }

  /**
   * Host: the account room of the open world (created the first time, the same one on every device). A world this
   * browser shared before accounts is claimed instead, so its link, files and live plans stay. Needs a (free) account.
   */
  async ensureRoom(resume?: () => void): Promise<RoomState | null> {
    const s = useApp.getState();
    if (s.room) return s.room;
    if (!s.worldId || !s.level || s.guestOf) return null;
    if (useAccount.getState().status !== "signed-in") {
      // gate 1: a free account — once signed in, carry on with what the person was doing
      openSignIn("Planning together needs a free account. Planning alone never does.");
      this.afterSignIn(resume);
      return null;
    }
    const server = s.collab.url;
    try {
      let roomId: string | null = null;
      const hostKey = hostKeyFor(server, s.worldId);
      if (hostKey) {
        try {
          roomId = (await claimLegacy(server, s.worldId, hostKey, s.worldId, s.level.name)).room_id;
        } catch (e) {
          // someone else claimed it, or the old room is gone: a new room is fine
          if (!(e instanceof ApiError) || e.status >= 500 || e.status === 0) throw e;
        }
      }
      roomId ??= (await createWorld(server, s.worldId, s.level.name)).room_id;
      rememberRoom(server, s.worldId, roomId);
      const t = await ticket(server, roomId);
      const room = this.roomFrom(t);
      useApp.getState().set({ room, ...(useApp.getState().share.state === "error" ? { share: { state: "idle" } } : {}) });
      this.applyRoomAccess();
      void refreshBilling(server).catch(() => {}); // the usage meters count the new world
      return room;
    } catch (e) {
      if (isPlanLimit(e)) {
        // gate 2: no plan (or no room left in it): the Plans dialog, with the free trial first when it's still
        // available — starting the trial (or a desktop purchase) carries on with what the person was doing
        openPlans(e.message, resume);
        return null;
      }
      useApp.getState().set({ share: { state: "error", msg: (e as Error).message } });
      return null;
    }
  }

  /**
   * Run `resume` once the sign-in dialog has signed someone in and closed (nothing if they close it signed out). The
   * dialog stays open after the code of a new account, for its name and colour (ui/Account.tsx), so wait for that.
   */
  private afterSignIn(resume?: () => void) {
    if (!resume) return;
    const unsub = useAccount.subscribe((a) => {
      if (a.status === "signed-in" && !a.dialog.open) { unsub(); resume(); }
      else if (!a.dialog.open && a.status === "signed-out") unsub();
    });
  }

  /** Host re-opens a world shared earlier (from this browser): know its room again, without creating one. */
  private async restoreRoom(worldId: string) {
    const server = useApp.getState().collab.url;
    const roomId = rememberedRoom(server, worldId);
    if (!roomId || useAccount.getState().status !== "signed-in") return;
    try {
      const t = await ticket(server, roomId);
      if (useApp.getState().worldId !== worldId || useApp.getState().guestOf) return;
      useApp.getState().set({ room: this.roomFrom(t) });
      this.applyRoomAccess();
      void this.refreshShareStatus();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 404)) rememberRoom(server, worldId, null);
    }
  }

  /** Guest: leave a shared world (the plan stays in this browser). */
  async leaveSharedWorld() {
    const s = useApp.getState();
    const me = useAccount.getState().user?.id;
    if (s.room?.kind !== "account" || !me) return;
    try {
      await removeMember(s.guestOf ?? s.collab.url, s.room.id, me);
      this.session?.disconnect();
      useApp.getState().set({ room: null, notice: { text: `You left “${s.room.name}”. Your copy of the plan stays in this browser.` } });
      this.applyRoomAccess();
    } catch (e) {
      useApp.getState().set({ error: (e as Error).message });
    }
  }

  /** Owner: stop sharing — deletes the shared copy (files, members, invite links). Everyone keeps their local plan. */
  async stopSharing() {
    const s = useApp.getState();
    if (s.room?.kind !== "account" || s.room.role !== "owner") return;
    const server = s.collab.url;
    try {
      this.session?.disconnect();
      await deleteWorld(server, s.room.id);
      if (s.worldId) rememberRoom(server, s.worldId, null);
      useApp.getState().set({ room: null, share: { state: "idle" }, notice: { text: "Stopped sharing. The plan is still here on your computer." } });
      this.applyRoomAccess();
    } catch (e) {
      useApp.getState().set({ error: (e as Error).message });
    }
  }

  private kicked(reason: KickReason) {
    const s = useApp.getState();
    const name = s.room?.name ?? "this world";
    const text: Record<KickReason, string> = {
      removed: `You were removed from “${name}”. Your copy of the plan stays in this browser.`,
      left: `You left “${name}”.`,
      deleted: `The owner stopped sharing “${name}”. Your copy of the plan stays in this browser.`,
      claimed: "The host moved this world to their account. Ask them for a new invite link to keep planning together.",
      "no-access": `You no longer have access to “${name}”. Ask the owner for an invite link.`,
    };
    useApp.getState().set({ notice: { text: text[reason] }, ...(reason === "left" ? {} : { room: s.room?.kind === "account" ? null : s.room }) });
    this.applyRoomAccess();
  }

  /**
   * Guest: open the host's world straight from the server and connect.
   * Textures come from the guest's own game jar (step 1) — hosts never share Mojang's textures.
   * @param opts.mapColours join without a jar: the host's pre-baked map colours, no 3D blocks
   */
  async joinInvite(name: string, opts: { mapColours?: boolean } = {}) {
    const inv = useApp.getState().invite;
    if (!inv?.manifest) return;
    if (opts.mapColours && !this.assetsReady) this.useMapColours();
    const account = inv.account;
    const headers: RoomHeaders | undefined = account ? () => roomHeaders(inv.server, inv.room) : undefined;
    this.remote = { server: inv.server, manifest: inv.manifest, headers };
    await this.openWorld(manifestSource(inv.server, inv.manifest, headers ? await headers() : undefined), inv.manifest.name, false, inv.room);
    if (useApp.getState().phase !== "ready") return;
    const room: RoomState = account
      ? { id: inv.room, kind: "account", role: account.role, paused: account.paused, name: inv.manifest.name, ownerName: account.ownerName, plan: null }
      : { id: inv.room, kind: "legacy", role: "editor", paused: false, name: inv.manifest.name, ownerName: inv.manifest.host || null, plan: null };
    useApp.getState().set({ guestOf: inv.server, invite: null, room });
    this.applyRoomAccess();
    clearInviteFromUrl();
    this.connect(inv.server, name);
  }

  /** Host: upload the current world (+ pre-baked map colours) to the collab server so guests don't need it. */
  async shareCurrentWorld() {
    const s = useApp.getState();
    if (!this.source || !this.layout || !s.level || !s.worldId || this.demo) return;
    const room = await this.ensureRoom(() => void this.shareCurrentWorld());
    if (!room) return;
    if (room.role !== "owner" && room.role !== "admin") return;
    s.set({ share: { state: "uploading", step: "prepare", frac: null, msg: "Checking what's already in the cloud" } });
    try {
      // wait until the whole map is loaded so it can be shared pre-baked
      while (this.engine && !this.engine.mapsComplete()) {
        const st = useApp.getState().stats;
        const frac = st?.regionsTotal ? (st.regionsLoaded ?? 0) / st.regionsTotal : null;
        useApp.getState().set({ share: { state: "uploading", step: "map", frac, msg: `The map is still loading: ${st?.regionsLoaded ?? 0} of ${st?.regionsTotal ?? "?"} regions` } });
        await new Promise((r) => setTimeout(r, 1000));
      }
      const maps = this.engine ? { dim: this.engine.currentDimension()!, regions: this.engine.exportRegionMaps() } : null;
      const manifest = await shareWorld(
        { server: s.collab.url, room: room.id, headers: () => roomHeaders(s.collab.url, room.id), source: this.source, layout: this.layout, info: { name: s.level.name, version: s.level.version, dataVersion: s.level.dataVersion, host: s.collab.name }, maps },
        (msg, frac, step) => useApp.getState().set({ share: { state: "uploading", step, frac, msg } }),
      );
      useApp.getState().set({ share: { state: "shared", manifest } });
    } catch (e) {
      useApp.getState().set({ share: { state: "error", msg: (e as Error).message } });
    }
  }

  async refreshShareStatus() {
    const s = useApp.getState();
    if (!s.worldId || s.guestOf || s.room?.kind !== "account") return;
    const server = s.collab.url, id = s.room.id;
    try {
      const manifest = await fetchManifest(server, id, () => roomHeaders(server, id));
      if (useApp.getState().share.state !== "uploading") useApp.getState().set({ share: manifest ? { state: "shared", manifest } : { state: "idle" } });
    } catch { /* server unreachable */ }
  }

  /** link of a world shared before accounts (account worlds use invite links from the server: lib/collab.ts) */
  inviteUrl() {
    const s = useApp.getState();
    return s.worldId && s.room?.kind === "legacy" ? inviteLink({ server: s.guestOf ?? s.collab.url, room: s.worldId }) : "";
  }

  /** while the game file cached in this browser is being read at startup */
  private restoring: Promise<unknown> | null = null;

  async restoreCachedAssets() {
    // (guests joining via an invite link use their own cached jar too — hosts don't share textures)
    const run = (async () => {
      const packs = await idb.get<CachedFile[]>("asset-packs");
      if (packs?.length) await this.loadAssets(packs.map((p) => ({ label: p.name, blob: p.blob })), false, true);
    })();
    this.restoring = run;
    try { await run; } finally { if (this.restoring === run) this.restoring = null; }
  }

  /**
   * @param background restore from cache at startup: silently dropped if, meanwhile, a world was opened or an
   *   invite is being joined. Swapping textures under a loaded world would make the texture array and the
   *   workers' block models disagree (wrong / black textures — the Chrome-only "black grass" bug).
   */
  async loadAssets(packs: { label: string; blob: Blob }[], cache = true, background = false) {
    const app = useApp.getState();
    app.set({ progress: { msg: "Reading game assets…", frac: 0 }, error: null });
    const bundle = await new Promise<AssetBundle>((resolve, reject) => {
      const w = new Worker(new URL("../engine/assets.worker.ts", import.meta.url), { type: "module" });
      w.onmessage = (e) => {
        const m = e.data;
        if (m.type === "progress") useApp.getState().set({ progress: { msg: m.msg, frac: m.frac } });
        else if (m.type === "done") { w.terminate(); resolve(m.bundle); }
        else if (m.type === "error") { w.terminate(); reject(new Error(m.error)); }
      };
      w.onerror = (e) => { w.terminate(); reject(new Error(e.message)); };
      w.postMessage({ type: "build", packs });
    }).catch((e: Error) => {
      failWith("noTextures", e.message, { progress: null });
      return null;
    });
    if (!bundle) return false;
    if (background && (useApp.getState().phase !== "setup" || this.assetsLocked || useApp.getState().mapColoursOnly)) {
      useApp.getState().set({ progress: null });
      return false;
    }
    if (this.assetsLocked) {
      useApp.getState().set({ progress: null, error: "Close the world first to change the game textures." });
      return false;
    }
    this.applyAssets(bundle);
    useApp.getState().set({ assetsLabel: bundle.label, progress: null });
    if (cache) await idb.set("asset-packs", packs.map((p) => ({ name: p.label, size: p.blob.size, savedAt: Date.now(), blob: p.blob } satisfies CachedFile)));
    return true;
  }

  /** true while a world is open: its chunk meshes reference layers of the current texture array */
  private get assetsLocked() {
    return useApp.getState().phase !== "setup";
  }

  /** Guest without a game jar: plan on the host's map colours (lib/mapColours.ts). */
  private useMapColours() {
    this.stopBackdrop(); // its 3D island needs blocks (a world opens next anyway)
    this.applyAssets(mapColoursBundle(), true);
    useApp.getState().set({ assetsLabel: MAP_COLOURS_LABEL });
  }

  /** back to normal after a map-colours-only visit (the fake bundle must not be used for a real world) */
  private leaveMapColours() {
    if (!useApp.getState().mapColoursOnly) return;
    this.assetsReady = false;
    this.bundle = null;
    this.baker = null;
    this.engine?.setSettings({ detail: true });
    useApp.getState().set({ assetsLabel: null, mapColoursOnly: false });
    void this.restoreCachedAssets();
  }

  /** @param mapColours the stand-in bundle of lib/mapColours.ts (no 3D blocks) */
  private applyAssets(bundle: AssetBundle, mapColours = false) {
    this.engineBlocks = mapColours ? "mapColours" : "jar";
    this.engine!.setAssets(bundle);
    this.engine!.setSettings({ detail: !mapColours });
    if (useApp.getState().mapColoursOnly !== mapColours) useApp.getState().set({ mapColoursOnly: mapColours });
    this.bundle = bundle;
    this.assetsReady = true;
    this.baker = new ModelBaker(bundle);
    this.thumbs.clear();
    this.allMaterials = null;
    this.renderer?.setMaterialSource(this.engine!.shared.uTex, this.materialInfo);
  }

  /**
   * What the engine draws blocks with: the player's game file, the map colours stand-in, or MC Planner's built-in
   * blocks (lib/demoBlocks.ts, only ever for the demo island: never a real world).
   */
  private engineBlocks: "jar" | "mapColours" | "demo" | null = null;
  private demoBaker: ModelBaker | null = null;

  /** the demo island and the backdrop: the player's own textures when loaded, else the built-in blocks */
  private blocksForDemo() {
    if (this.engineBlocks === "jar" && this.assetsReady && !useApp.getState().mapColoursOnly) {
      this.engine!.setSettings({ detail: true });
      return;
    }
    if (this.engineBlocks === "demo") return;
    const bundle = demoBlocksBundle();
    this.engineBlocks = "demo";
    this.engine!.setAssets(bundle);
    this.engine!.setSettings({ detail: true });
    this.demoBaker ??= new ModelBaker(bundle);
    this.thumbs.clear();
    this.allMaterials = null;
    this.renderer?.setMaterialSource(this.engine!.shared.uTex, this.materialInfo);
  }

  /** the block models and textures the engine draws with right now (for materials: their layers must match) */
  private get shownBaker() {
    return this.engineBlocks === "demo" ? this.demoBaker : this.baker;
  }
  private get shownBundle() {
    return this.engineBlocks === "demo" ? demoBlocksBundle() : this.bundle;
  }

  // ---------------- block materials (textured paths / areas / walls) ----------------

  private baker: ModelBaker | null = null;
  private thumbs = new Map<number, string>();
  private allMaterials: BlockMaterial[] | null = null;

  /** top/side texture of a block for the plan renderer */
  readonly materialInfo = (id: string): MaterialInfo | null => {
    const baker = this.shownBaker, bundle = this.shownBundle;
    const m = baker ? blockMaterial(baker, id) : null;
    if (!m) return null;
    const tint = DEFAULT_TINT[m.tint] ?? null;
    // see-through tops (crops, flowers) get ground under them: soul sand for nether wart, farmland otherwise
    let under: number | null = null;
    if (bundle && bundle.textures.alpha[m.top] === 1) {
      const g = blockMaterial(baker!, id === "minecraft:nether_wart" ? "minecraft:soul_sand" : "minecraft:farmland");
      under = g?.top ?? null;
    }
    return { top: m.top, side: m.side, tintTop: m.tintTop ? tint : null, tintSide: m.tintSide ? tint : null, under };
  };

  material(id: string): BlockMaterial | null {
    const baker = this.shownBaker;
    return baker ? blockMaterial(baker, id) : null;
  }

  /** every block of the loaded jars (surface blocks first); on the demo island without them, the built-in blocks */
  materials(): BlockMaterial[] {
    const baker = this.shownBaker;
    if (!baker) return [];
    return (this.allMaterials ??= listBlockMaterials(baker));
  }

  /** 16×16 texture preview as a data URL (tinted like in the world) */
  materialThumb(m: BlockMaterial, face: "top" | "side" = "top"): string {
    const layer = face === "top" ? m.top : m.side;
    const tinted = face === "top" ? m.tintTop : m.tintSide;
    const key = layer * 2 + (tinted ? 1 : 0);
    const hit = this.thumbs.get(key);
    if (hit) return hit;
    const t = this.shownBundle?.textures;
    if (!t) return "";
    const n = t.size * t.size * 4;
    const px = new Uint8ClampedArray(t.pixels.buffer, t.pixels.byteOffset + layer * n, n).slice();
    const tint = tinted ? DEFAULT_TINT[m.tint] : undefined;
    if (tint !== undefined) {
      const r = (tint >> 16) / 255, g = ((tint >> 8) & 255) / 255, b = (tint & 255) / 255;
      for (let i = 0; i < n; i += 4) { px[i] *= r; px[i + 1] *= g; px[i + 2] *= b; }
    }
    const c = document.createElement("canvas");
    c.width = c.height = t.size;
    c.getContext("2d")!.putImageData(new ImageData(px, t.size, t.size), 0, 0);
    const url = c.toDataURL();
    this.thumbs.set(key, url);
    return url;
  }

  async forgetAssets() {
    if (useApp.getState().mapColoursOnly) {
      this.leaveMapColours();
      return;
    }
    await idb.del("asset-packs");
    this.assetsReady = false;
    useApp.getState().set({ assetsLabel: null });
  }

  // ---------------- worlds ----------------

  async recentWorlds(): Promise<RecentWorld[]> {
    return (await idb.get<RecentWorld[]>("recent-worlds")) ?? [];
  }

  private zipIcons = new Map<string, Promise<string | null>>();

  /**
   * A recent .zip world's own picture: the icon.png Minecraft saves next to level.dat, read from the zip kept in this
   * browser (only its directory and that one small file). Null when the world has none.
   */
  recentWorldIcon(key: string): Promise<string | null> {
    let p = this.zipIcons.get(key);
    if (!p) {
      p = (async () => {
        const blob = await idb.get<Blob>(`world:${key}`);
        if (!blob) return null;
        const zip = await ZipReader.open(blob);
        const names = [...zip.entries.keys()];
        const depth = (n: string) => n.split("/").length;
        const level = names.filter((n) => /(^|\/)level\.dat$/i.test(n)).sort((a, b) => depth(a) - depth(b))[0];
        const icons = names.filter((n) => /(^|\/)icon\.png$/i.test(n)).sort((a, b) => depth(a) - depth(b));
        const icon = (level && icons.find((n) => n.toLowerCase() === level.slice(0, -"level.dat".length).toLowerCase() + "icon.png")) ?? icons[0];
        if (!icon) return null;
        const bytes = await zip.read(icon);
        return URL.createObjectURL(new Blob([bytes as BlobPart], { type: "image/png" }));
      })().catch(() => null);
      this.zipIcons.set(key, p);
    }
    return p;
  }

  async openRecent(r: RecentWorld) {
    this.remote = null;
    const blob = await idb.get<Blob>(`world:${r.key}`);
    if (!blob) {
      failWith("worldWontOpen", "That world is no longer cached — please choose the file again.");
      return;
    }
    await this.openWorld({ kind: "zip", blob }, r.fileName, false);
    if (useApp.getState().phase === "ready") useOnboarding.getState().set({ lastWorld: { kind: "recent", key: r.key, name: r.name } });
  }

  /** Desktop: open a world folder in place (no zipping). */
  async openLocalWorld(path: string) {
    this.remote = null;
    noteOpenedName(path); // before anything can fail: its folder name stays out of a crash report
    let files: Awaited<ReturnType<typeof worldFileUrls>>;
    try {
      files = await worldFileUrls(path);
    } catch (e) {
      failWith("worldWontOpen", String((e as Error)?.message ?? e));
      return;
    }
    await this.openWorld({ kind: "urls", files }, path, false);
    if (useApp.getState().phase === "ready") {
      void this.watchLocalWorld(path);
      useOnboarding.getState().set({ lastWorld: { kind: "local", path, name: useApp.getState().level?.name ?? path } });
    }
  }

  // ---------------- desktop: notice when Minecraft saves the world ----------------

  private localRoot: string | null = null;
  private stamp: WorldStamp | null = null;
  private watchTimer = 0;

  private async watchLocalWorld(root: string) {
    this.stopWatching();
    this.localRoot = root;
    try { this.stamp = await worldStamp(root); } catch { return; }
    this.watchTimer = window.setInterval(async () => {
      if (!this.localRoot || document.hidden || useApp.getState().worldChanged) return;
      try {
        const now = await worldStamp(this.localRoot);
        const was = this.stamp;
        if (was && (now.newest !== was.newest || now.files !== was.files || now.bytes !== was.bytes)) useApp.getState().set({ worldChanged: true });
      } catch { /* folder gone / locked while saving: try again next time */ }
    }, 15_000);
  }

  private stopWatching() {
    clearInterval(this.watchTimer);
    this.watchTimer = 0;
    this.localRoot = null;
    this.stamp = null;
    useApp.getState().set({ worldChanged: false });
  }

  /** Re-read the world from disk (after Minecraft saved) and stay where you are. */
  async reloadLocalWorld() {
    const root = this.localRoot;
    const engine = this.engine;
    if (!root || !engine) return;
    const dim = useApp.getState().dimension;
    const target = engine.controls.target.clone();
    const cam = engine.camera.position.clone();
    await this.openLocalWorld(root);
    if (useApp.getState().phase !== "ready") return;
    if (dim !== useApp.getState().dimension && useApp.getState().dimensions.some((d) => d.id === dim)) {
      useApp.getState().set({ dimension: dim });
      await new Promise((r) => setTimeout(r, 50));
    }
    engine.controls.target.copy(target);
    engine.camera.position.copy(cam);
    engine.controls.update();
  }

  /** Desktop: load the game jar straight from the launcher folder. */
  async loadLocalJar(path: string, label: string) {
    const blob = await readLocalFile(path);
    return this.loadAssets([{ label, blob }]);
  }

  /**
   * Guest without Minecraft on this computer: this browser downloads the host's version of the game file straight
   * from Mojang (lib/mojang.ts) — never through our server — and keeps it here like a jar the person picked.
   */
  async loadMojangTextures(version: string) {
    const app = useApp.getState();
    if (app.progress || this.assetsLocked) return false;
    acceptMojang();
    app.set({ progress: { msg: "Asking Mojang for the game's textures…", frac: 0 }, error: null });
    try {
      const c = await findClient(version);
      const blob = await downloadClient(c, (got, total) =>
        useApp.getState().set({ progress: { msg: `Downloading Minecraft ${c.id} from Mojang… ${fmtMB(got)} of ${fmtMB(total)}`, frac: total ? (got / total) * 0.9 : 0 } }));
      useApp.getState().set({ progress: null });
      return await this.loadAssets([{ label: `Minecraft ${c.id}`, blob }]);
    } catch (e) {
      const offline = e instanceof TypeError; // fetch: no connection / blocked
      failWith(
        "noTextures",
        offline
          ? "Couldn't reach Mojang's download server. Check the connection, or drop your Minecraft .jar here instead."
          : `Couldn't get the textures from Mojang: ${(e as Error).message}`,
        { progress: null },
      );
      return false;
    }
  }

  async openZip(file: File) {
    this.remote = null;
    this.stopWatching();
    await this.openWorld({ kind: "zip", blob: file }, file.name, true);
  }

  async openFolder(files: FileList | File[]) {
    this.remote = null;
    this.stopWatching();
    const list = Array.from(files).map((f) => ({ path: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name, file: f as Blob }));
    await this.openWorld({ kind: "files", files: list }, list[0]?.path.split("/")[0] ?? "folder", false);
  }

  /** A world folder dropped on the page (the drop's files, read lazily by the workers). */
  async openFolderFiles(files: { path: string; file: Blob }[], name: string) {
    this.remote = null;
    this.stopWatching();
    await this.openWorld({ kind: "files", files }, name, false);
  }

  private async openWorld(init: SourceInit, fileName: string, cache: boolean, roomOverride?: string) {
    // its name stays out of a crash report even if reading it fails (lib/crash.ts)
    noteOpenedName(fileName);
    const app = useApp.getState();
    if (!this.assetsReady) {
      failWith("noTextures", "Load the Minecraft game jar first (step 1).");
      return;
    }
    app.set({ phase: "loading", progress: { msg: "Reading world…", frac: 0.1 }, error: null });
    this.demo = false;
    try {
      const src = await openSource(init);
      const layouts = detectWorlds(src.paths());
      if (!layouts.length) throw new Error("No level.dat found — is this a Minecraft world (zip of the world folder)?");
      const layout = layouts[0];
      if (!layout.dimensions.length) throw new Error("The world has no region files yet.");
      const level = await readLevel(src, layout);
      const worldId = roomOverride ?? (await worldIdFor(level.name, level.seed));
      const remote = this.remote;
      await this.attachWorld(init, layout, level, worldId, remote ? (dim) => prebakedMaps(remote.server, remote.manifest, dim, remote.headers) : undefined, !roomOverride);
      if (cache && init.kind === "zip") void this.cacheWorld(worldId, level.name, fileName, init.blob);
    } catch (e) {
      console.error(e);
      failWith("worldWontOpen", (e as Error).message, { phase: "setup", progress: null });
    }
  }

  /** the demo island is open (map colours, generated terrain, local-only plan) */
  private demo = false;

  /**
   * Open the plan document of a world and put the world on screen.
   * @param prebaked region maps that don't have to be read from region files (guests, the demo island)
   */
  private async attachWorld(init: SourceInit, layout: WorldLayout, level: LevelInfo, worldId: string, prebaked?: (dim: string) => Map<string, () => Promise<RegionMapResult>> | undefined, restoreRoom = false, persist = true) {
    const app = useApp.getState();
    {
      this.stopBackdrop();
      this.session?.destroy();
      this.unobserve?.();
      const session = await PlanSession.open(worldId, { persist });
      session.store.meta.set("worldName", level.name);
      if (level.seed) session.store.meta.set("seed", level.seed);
      this.session = session;
      this.source = init;
      this.layout = layout;

      const engine = this.engine!;
      if (!this.renderer) {
        this.renderer = new PlanRenderer(engine.planRoot, engine.heights);
        this.renderer.onBadgeClick = (id) => { select([id]); this.openBoard(id); };
        if (this.shownBaker) this.renderer.setMaterialSource(engine.shared.uTex, this.materialInfo);
        const structures = new StructureRenderer(engine, async (hash) => {
          const blob = await getSchematicFile(hash);
          return blob ? { blob, name: useApp.getState().schematics.find((e) => e.hash === hash)?.fileName ?? "schematic" } : null;
        });
        structures.onBusy = (busy) => useApp.getState().set({ schemMeshing: busy });
        structures.onError = (msg) => useApp.getState().set({ error: msg });
        structures.setAvailable(useApp.getState().schematics.map((e) => e.hash));
        structures.setCut(app.cutEnabled ? app.cutY : null);
        this.structures = structures;
        this.renderer.structureShown = (o) => !!o.schematic && structures.has(o.schematic.hash);
      }
      this.renderer.setLabelsVisible(app.showLabels);
      this.renderer.setEdgesVisible(app.showLines);
      this.tools = new PlanTools(engine, this.renderer, session.store);
      this.tools.onCreated = () => track("draw");
      this.tools.onCursor = (p) => session.setCursor(p);
      this.tools.onGoHome = () => this.goHome();
      this.tools.onSetHome = (at) => this.setHome(at ? "mouse" : "view", at);
      engine.handler = this.tools;

      const syncPlans = () => {
        const plans = session.store.list();
        // old 2-corner buildings (also ones arriving from collaborators/imports) → footprint polygons
        if (plans.some((p) => p.kind === "building" && p.points.length === 2)) queueMicrotask(() => session.store.migrate());
        const ids = new Set(plans.map((p) => p.id));
        const sel = useApp.getState().selectedIds.filter((id) => ids.has(id));
        useApp.getState().set({ plans });
        if (sel.length !== useApp.getState().selectedIds.length) select(sel);
        this.renderer!.sync(plans, this.selection());
        this.structures?.sync(plans);
      };
      const syncMeta = () => {
        const home = session.store.getHome(this.renderer?.dimension ?? useApp.getState().dimension);
        useApp.getState().set({ palette: session.store.palette(), recentMaterials: session.store.recentMaterials(), worldTemplates: session.store.templates(), kindColors: (session.store.meta.get("kindColors") as Record<string, string>) ?? {}, home });
        this.renderer?.setHome(home);
      };
      const syncVersions = () => useApp.getState().set({ versions: session.store.listVersions() });
      const syncCards = () => {
        useApp.getState().set({ boardTick: useApp.getState().boardTick + 1 });
        void this.refreshBadges();
        this.scheduleImageSync();
      };
      session.store.cards.observeDeep(syncCards);
      session.store.objects.observeDeep(syncPlans);
      session.store.meta.observe(syncMeta);
      session.store.versions.observe(syncVersions);
      syncVersions();
      syncCards();
      session.onSave = (saveState) => useApp.getState().set({ saveState });
      useApp.getState().set({ saveState: session.saveState });
      syncMeta();
      session.store.migrate();
      this.unobserve = () => { session.store.objects.unobserveDeep(syncPlans); session.store.meta.unobserve(syncMeta); session.store.versions.unobserve(syncVersions); session.store.cards.unobserveDeep(syncCards); };
      session.onPeers = (peers) => {
        this.renderer?.setPeers(peers);
        useApp.getState().set({ collab: { ...useApp.getState().collab, peers: peers.map((p) => ({ name: p.name, color: p.color, uid: p.uid })) } });
      };
      session.onRoomChanged = (what) => {
        useApp.getState().set({ roomRev: useApp.getState().roomRev + 1 });
        // the owner's usage meters (account menu) count uploads too
        if (what === "storage" && useApp.getState().room?.role === "owner") void refreshBilling(useApp.getState().collab.url).catch(() => {});
      };
      let blockedAt = 0;
      session.onBlocked = () => {
        if (Date.now() - blockedAt < 4000) return;
        blockedAt = Date.now();
        const room = useApp.getState().room;
        useApp.getState().set({ notice: { text: room?.paused ? "This world is paused: the owner's plan doesn't cover it. You can look around and export, but not change it." : "View only: you can look around, but not change this world. Ask the owner to make you an editor." } });
      };
      session.onKicked = (reason) => this.kicked(reason);
      session.onStatus = (status) => {
        useApp.getState().set({ collab: { ...useApp.getState().collab, status } });
        if (status === "connected") { void this.refreshShareStatus(); this.scheduleImageSync(500); track("together"); }
      };

      const dim = layout.dimensions.find((d) => d.id === "minecraft:overworld") ?? layout.dimensions[0];
      useApp.getState().set({
        level,
        worldId,
        dimensions: layout.dimensions,
        dimension: dim.id,
        selectedId: null,
        tool: "select",
        guestOf: null,
        room: null,
        readOnly: false,
        share: { state: "idle" },
        phase: "ready",
        progress: null,
        demo: this.demo,
      });
      if (restoreRoom) void this.restoreRoom(worldId);
      this.renderer.dimension = dim.id;
      if (this.structures) this.structures.dimension = dim.id;
      syncMeta();
      syncPlans();
      // open at the world's home location if one was set (shared), else at the game's spawn point
      const home = session.store.getHome(dim.id);
      const spawn: [number, number, number] = home ? [home.x, home.y, home.z] : level.spawn ?? [0, 64, 0];
      this.prebakedFor = prebaked ?? null;
      await engine.openWorld(init, dim, spawn, prebaked?.(dim.id));
    }
  }

  private prebakedFor: ((dim: string) => Map<string, () => Promise<RegionMapResult>> | undefined) | null = null;

  private async cacheWorld(key: string, name: string, fileName: string, blob: Blob) {
    const list = (await this.recentWorlds()).filter((r) => r.key !== key);
    list.unshift({ key, name, fileName, size: blob.size, savedAt: Date.now() });
    for (const old of list.splice(5)) await idb.del(`world:${old.key}`);
    await idb.set(`world:${key}`, blob);
    await idb.set("recent-worlds", list);
    useOnboarding.getState().set({ lastWorld: { kind: "recent", key, name } });
  }

  // ---------------- onboarding: backdrop, demo island, texture preview ----------------

  private demoViewPromise: Promise<DemoTopView> | null = null;

  /** The demo island seen from above, n × n (for its picture on the world step; made once in a worker, then kept). */
  demoTopView(n: number): Promise<DemoTopView> {
    this.demoViewPromise ??= new Promise<DemoTopView>((resolve, reject) => {
      const w = new Worker(new URL("../engine/demo.worker.ts", import.meta.url), { type: "module" });
      w.onmessage = (e) => { w.terminate(); resolve(e.data as DemoTopView); };
      w.onerror = (e) => { w.terminate(); this.demoViewPromise = null; reject(new Error(e.message)); };
      w.postMessage(n);
    });
    return this.demoViewPromise;
  }

  private backdropPlan: PlanRenderer | null = null;
  private backdropTimer = 0;

  /**
   * The setup wizard's backdrop: the demo island circling slowly behind the card, with the example plan
   * drawing itself in (the real map renderer + plan renderer, no world files needed).
   */
  async showBackdrop() {
    const engine = this.engine;
    if (!engine || engine.showingBackdrop || useApp.getState().phase !== "setup") return;
    // a game file kept in this browser: let it load first, so the island shows the player's own blocks right away
    if (this.restoring) await this.restoring.catch(() => {});
    if (useApp.getState().phase !== "setup" || engine.showingBackdrop) return;
    this.blocksForDemo();
    const still = prefersReducedMotion();
    try {
      await engine.showBackdrop(DEMO_DIM, DEMO_SPAWN, still ? 0 : 0.045, DEMO_SKY);
    } catch (e) {
      console.warn("demo island failed", e);
      return;
    }
    if (!engine.showingBackdrop || this.backdropPlan) return;
    const plan = new PlanRenderer(engine.backdropRoot, engine.heights);
    plan.setEdgesVisible(true);
    this.backdropPlan = plan;
    // (what's already built stands there in blocks: no box over it)
    const objs = demoPlan().filter((o) => o.status !== "done");
    const none = { ids: [] as string[], primary: null };
    const show = (list: PlanObject[]) => { plan.sync(list, none); plan.setLabelsVisible(false); };
    if (still) { show(objs); return; }
    // the plan draws itself: paths grow point by point, then the rest pops in one by one; hold; clear; again
    const frames: PlanObject[][] = [];
    const done: PlanObject[] = [];
    for (const o of objs) {
      if (o.kind === "path") for (let n = 2; n <= o.points.length; n++) frames.push([...done, { ...o, points: o.points.slice(0, n) }]);
      else frames.push([...done, o]);
      done.push(o);
    }
    for (let i = 0; i < 26; i++) frames.push(objs);
    for (let i = 0; i < 5; i++) frames.push([]);
    let i = 0;
    clearInterval(this.backdropTimer);
    this.backdropTimer = window.setInterval(() => {
      if (this.backdropPlan !== plan) return;
      show(frames[i % frames.length]);
      i++;
    }, 170);
  }

  stopBackdrop() {
    clearInterval(this.backdropTimer);
    this.backdropTimer = 0;
    this.backdropPlan?.sync([], { ids: [], primary: null });
    this.backdropPlan = null;
    this.engine?.stopBackdrop();
  }

  /**
   * "Try the demo island": plan on the generated island without Minecraft files, in 3D blocks (the player's own
   * textures when loaded, else MC Planner's built-in blocks); the plan is kept in this browser like any other world,
   * starting with an example village.
   * `pack`: a real world's region maps instead (the website's live demo, lib/demoPack.ts) — map colours only.
   */
  async openDemo(opts: { room?: string; persist?: boolean; remember?: boolean; pack?: LoadedDemoPack } = {}) {
    const app = useApp.getState();
    const pack = opts.pack;
    app.set({ phase: "loading", progress: { msg: pack ? "Opening the demo world…" : "Building the demo island…", frac: 0.3 }, error: null });
    try {
      if (pack) {
        if (!this.assetsReady) this.useMapColours();
        else this.engine!.setSettings({ detail: false });
      } else this.blocksForDemo();
      this.demo = true;
      this.remote = null;
      this.stopWatching();
      const regions = pack ? pack.info.regions.map((r) => [r.rx, r.rz] as const) : DEMO_REGIONS;
      const layout: WorldLayout = { root: "", dimensions: [{ id: "minecraft:overworld", dir: "", regions: regions.map(([rx, rz]) => ({ rx, rz, path: `region/r.${rx}.${rz}.mca` })) }] };
      const level: LevelInfo = pack
        ? { name: pack.info.name, version: pack.info.version, dataVersion: pack.info.dataVersion, spawn: pack.info.spawn, modded: false, brands: [] }
        : { name: DEMO_NAME, version: "demo", dataVersion: 0, spawn: DEMO_SPAWN, modded: false, brands: [] };
      if (pack) {
        const byKey = new Map(pack.maps.map((m) => [`${m.rx},${m.rz}`, m]));
        const prebaked = () => new Map([...byKey].map(([k, m]) => [k, async () => m] as const));
        await this.attachWorld({ kind: "files", files: [] }, layout, level, opts.room ?? DEMO_ROOM, prebaked, false, opts.persist ?? true);
      } else await this.attachWorld({ kind: "demo" }, layout, level, opts.room ?? DEMO_ROOM, undefined, false, opts.persist ?? true);
      const store = this.session?.store;
      if (store && !store.meta.get("demoSeeded")) {
        store.meta.set("demoSeeded", true);
        for (const { id: _id, ...o } of pack ? pack.plan : demoPlan()) store.create(o);
      }
      if (opts.remember ?? true) useOnboarding.getState().set({ lastWorld: { kind: "demo", name: DEMO_NAME } });
    } catch (e) {
      console.error(e);
      this.demo = false;
      useApp.getState().set({ phase: "setup", progress: null, error: (e as Error).message });
    }
  }

  /** a game jar is cached in this browser / app (restored at startup) */
  async hasCachedAssets() {
    return !!(await idb.get<CachedFile[]>("asset-packs"))?.length;
  }

  /** Number of blocks the loaded jar knows (for "1 095 blocks ready"). */
  blockCount() {
    return this.bundle ? Object.keys(this.bundle.blockstates).length : 0;
  }

  /**
   * A handful of the loaded block textures for the wizard's mosaic (opaque, untinted ones only).
   * Pixels stay on this computer — they're only drawn into a canvas.
   */
  textureSample(max = 240): { size: number; tiles: Uint8ClampedArray[] } | null {
    const t = this.bundle?.textures;
    if (!t?.pixels || useApp.getState().mapColoursOnly) return null;
    const px = t.size * t.size * 4;
    const skip = /grass|leaves|vine|fern|lily|water|lava|redstone|stem|destroy|particle|_overlay|sapling|flower|bush|seagrass|kelp|coral|glass|debug|missing/;
    const picks: number[] = [];
    for (let i = 0; i < t.names.length; i++) if (t.alpha[i] === 0 && !skip.test(t.names[i])) picks.push(i);
    // spread the picks over the whole list (alphabetical → varied materials)
    const step = Math.max(1, picks.length / max);
    const tiles: Uint8ClampedArray[] = [];
    for (let k = 0; k < picks.length && tiles.length < max; k += step) {
      const i = picks[Math.floor(k)];
      tiles.push(new Uint8ClampedArray(t.pixels.buffer, t.pixels.byteOffset + i * px, px));
    }
    return tiles.length ? { size: t.size, tiles } : null;
  }

  /** screen position of a block on the map (tour) */
  screenPos(x: number, y: number, z: number) {
    return this.engine?.project(x, y, z) ?? null;
  }

  async switchDimension(id: string) {
    if (!this.layout || !this.source || !this.engine) return;
    const dim = this.layout.dimensions.find((d) => d.id === id);
    if (!dim) return;
    this.cancelSchematicPlacing();
    this.renderer!.dimension = id;
    this.renderer!.sync([], { ids: [], primary: null });
    if (this.structures) { this.structures.dimension = id; this.structures.sync([]); }
    const plans = this.session!.store.list();
    const t = this.engine.controls.target;
    // nether ceiling: start with a slice so you can see inside
    if (id === "minecraft:the_nether") useApp.getState().set({ cutEnabled: true, cutY: 100 });
    const home = this.session!.store.getHome(id);
    this.renderer!.setHome(home);
    useApp.getState().set({ home });
    await this.engine.openWorld(this.source, dim, home ? [home.x, home.y, home.z] : [Math.round(t.x), 64, Math.round(t.z)], this.prebakedFor?.(dim.id));
    this.renderer!.sync(plans, this.selection());
    this.structures?.sync(plans);
  }

  closeWorld() {
    this.stopWatching();
    this.cancelLayout();
    this.cancelSchematicPlacing();
    this.structures?.clear();
    useApp.getState().set({ boardFor: null });
    this.session?.destroy();
    this.session = null;
    this.unobserve?.();
    this.unobserve = null;
    this.engine?.clearWorld();
    this.renderer?.sync([], { ids: [], primary: null });
    this.remote = null;
    this.prebakedFor = null;
    if (this.demo) {
      this.demo = false;
      if (!useApp.getState().mapColoursOnly) this.engine?.setSettings({ detail: true });
    }
    useApp.getState().set({ phase: "setup", level: null, worldId: null, plans: [], selectedId: null, guestOf: null, room: null, readOnly: false, share: { state: "idle" }, demo: false });
    this.leaveMapColours();
  }

  // ---------------- plans ----------------

  update(id: string, patch: Partial<PlanObject>) {
    this.session?.store.update(id, patch);
  }

  remove(id: string) {
    this.session?.store.remove(id);
    select(useApp.getState().selectedIds.filter((x) => x !== id));
  }

  /** current selection for the renderer */
  selection() {
    const s = useApp.getState();
    return { ids: s.selectedIds, primary: s.selectedId };
  }

  get store() {
    return this.session?.store ?? null;
  }

  focus(o: PlanObject) {
    if (!this.engine || !o.points.length) return;
    const xs = o.points.map((p) => p[0]), zs = o.points.map((p) => p[1]);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2, cz = (Math.min(...zs) + Math.max(...zs)) / 2;
    const span = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...zs) - Math.min(...zs), 20);
    const y = o.kind === "building" || o.kind === "marker" ? o.y : this.engine.heights.atOr(cx, cz, 64);
    this.engine.flyTo(cx, y, cz, Math.max(60, span * 1.6));
    useApp.getState().set({ selectedId: o.id });
  }

  // ---------------- home location ----------------

  /** Fly to this dimension's home (or the spawn point if none is set). */
  goHome() {
    const s = useApp.getState();
    const home = this.session?.store.getHome(s.dimension);
    if (home) this.engine?.flyTo(home.x + 0.5, home.y, home.z + 0.5, 160);
    else if (s.level?.spawn) this.engine?.flyTo(s.level.spawn[0], s.level.spawn[1], s.level.spawn[2], 160);
  }

  /** Set home at the block under the mouse or at the centre of the view. Shared with everyone in the room. */
  setHome(mode: "mouse" | "view", at?: { x: number; z: number } | null) {
    if (!this.session || !this.engine) return;
    let x: number, z: number;
    if (mode === "mouse" && at) { x = at.x; z = at.z; }
    else { const t = this.engine.controls.target; x = Math.floor(t.x); z = Math.floor(t.z); }
    const y = this.engine.heights.at(x, z) ?? Math.round(this.engine.controls.target.y);
    this.session.store.setHome(useApp.getState().dimension, { x, y, z, setBy: useApp.getState().collab.name || undefined });
  }

  clearHome() {
    this.session?.store.setHome(useApp.getState().dimension, null);
  }

  goTo(x: number, z: number) {
    this.engine?.flyTo(x, this.engine.heights.atOr(x, z, 64), z);
  }

  // ---------------- plan files & versions ----------------

  /** Download the whole plan (all dimensions, settings, versions) as <world>.mcplan. Ctrl+S. */
  async savePlanFile(withImages = true) {
    if (!this.session) return;
    const lvl = useApp.getState().level;
    const data = this.session.store.toFile({ name: lvl?.name, seed: lvl?.seed });
    if (withImages) data.images = await embedImages(this.session.store.imageHashes());
    const stamp0 = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
    if (isDesktop()) {
      // desktop: straight into Documents\MC Planner (a webview has no real download bar)
      void saveTextFile(`${lvl?.name ?? "world"} ${stamp0}.mcplan`, JSON.stringify(data))
        .then((path) => useApp.getState().set({ notice: { text: `Plan saved to ${path}`, path } }))
        .catch((e) => useApp.getState().set({ error: `Saving failed: ${String((e as Error)?.message ?? e)}` }));
      return;
    }
    const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
    a.download = `${(lvl?.name ?? "world").replace(/[\\/:*?"<>|]+/g, "_")} ${stamp}.mcplan`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }

  /** Read and check a plan file (.mcplan or an older .json export) before asking replace/merge. */
  async readPlanFile(file: File): Promise<PlanFile> {
    let raw: unknown;
    try { raw = JSON.parse(await file.text()); } catch { throw new Error("The file is not valid JSON."); }
    return parsePlanFile(raw);
  }

  loadPlanFile(data: PlanFile, mode: "replace" | "merge") {
    const store = this.session?.store;
    if (!store) return;
    // safety net: keep the current state as a version before replacing it
    if (mode === "replace" && store.list().length) store.saveVersion("Before loading a plan file", useApp.getState().collab.name);
    void importImages(data.images).then(() => this.refreshBadges());
    const r = store.loadFile(data, mode);
    select([]);
    return r;
  }

  // ---------------- layouts & templates ----------------

  /** "schematics" opens the full-window Schematics view (ui/schematics/SchematicsView.tsx), the others the layouts panel. */
  openLayouts(tab: "fill" | "templates" | "schematics" | null) {
    if (tab === "schematics") { this.openSchematics(); return; }
    if (!tab) this.cancelLayout();
    this.cancelSchematicPlacing();
    useApp.getState().set({ layoutPanel: tab, schemEdit: null });
  }

  /** Full-window Schematics view (layoutPanel "schematics"), optionally with one file (hash) selected. */
  openSchematics(hash?: string | null) {
    this.cancelLayout();
    this.cancelSchematicPlacing();
    useApp.getState().set({ layoutPanel: "schematics", ...(hash !== undefined ? { schemSelected: hash } : {}) });
  }

  /** Back to the map. */
  closeSchematics() {
    if (useApp.getState().layoutPanel === "schematics") useApp.getState().set({ layoutPanel: null, schemEdit: null });
  }

  /** Start filling the selected area (or a path for villages) with a preset. */
  startFill(targetId: string, preset?: PresetId) {
    const o = this.store?.get(targetId);
    if (!o || (o.kind !== "area" && o.kind !== "path")) return;
    const id = preset ?? (o.kind === "path" ? "village" : "grid");
    useApp.getState().set({ layoutPanel: "fill", layout: { mode: "fill", targetId, preset: id, params: defaultParams(id), scale: 1 } });
  }

  updateLayout(patch: Partial<LayoutState>) {
    const l = useApp.getState().layout;
    if (!l) return;
    const next = { ...l, ...patch } as LayoutState;
    if (next.mode === "fill" && patch && "preset" in patch && patch.preset !== (l as { preset?: string }).preset) (next as { params: Record<string, number> }).params = defaultParams(next.preset);
    useApp.getState().set({ layout: next });
  }

  cancelLayout() {
    if (this.tools) this.tools.placement = null;
    this.renderer?.setPreview(null);
    if (useApp.getState().layout) useApp.getState().set({ layout: null });
  }

  /** objects the current fill layout would create */
  fillObjects(l = useApp.getState().layout): LayoutObject[] {
    if (!l || l.mode !== "fill" || !this.store) return [];
    const t = this.store.get(l.targetId);
    if (!t) return [];
    const shape = t.kind === "path" ? t.points : buildingPoints(t);
    return runPreset(l.preset, shape, scaledParams(l.preset, l.params, l.scale), { roadWidth: t.width });
  }

  /** houses / streets / blocks to pave of a layout (for the summary line) */
  layoutSummary(objs: LayoutObject[]) {
    let streets = 0, pave = 0, houses = 0, areas = 0, areaBlocks = 0, water = 0;
    for (const o of objs) {
      if (o.kind === "path") {
        const n = counts({ kind: "path", points: o.points, width: o.width ?? 1, height: 0 }).blocks;
        if (o.material === "minecraft:water") water += n;
        else { streets += polylineLength(o.points); pave += n; }
      } else if (o.kind === "building") houses++;
      else if (o.kind === "area") { areas++; areaBlocks += counts({ kind: "area", points: o.points, width: 0, height: 0 }).blocks; }
    }
    return { houses, areas, areaBlocks, water, streets: Math.round(streets), pave, total: objs.length };
  }

  private previewTimer = 0;
  private placeAt: [number, number] | null = null;

  private layoutChanged(l: LayoutState | null, prev: LayoutState | null) {
    if (!l) { this.cancelLayout(); return; }
    if (l.mode === "place") {
      if (!prev || prev.mode !== "place") this.armPlacement();
      this.previewPlacement();
      return;
    }
    if (this.tools && !useApp.getState().schemPlace) this.tools.placement = null;
    clearTimeout(this.previewTimer);
    this.previewTimer = window.setTimeout(() => this.renderer?.setPreview(this.asPreview(this.fillObjects())), 80);
  }

  private asPreview(objs: LayoutObject[]): PlanObject[] {
    const dim = useApp.getState().dimension;
    return objs.map((o, i) => {
      const color = o.color ?? this.store?.defaultColor(o.kind) ?? "#ffffff";
      const base = { id: `preview-${i}`, kind: o.kind, name: o.name, color, status: "idea" as const, dimension: dim, points: o.points, width: o.width ?? 3, height: o.height ?? 8, notes: "", author: "", createdAt: 0, updatedAt: 0, ...(o.material ? { material: o.material } : {}) };
      return { ...base, y: o.y ?? this.tools?.seatY(base, 64) ?? 64 };
    });
  }

  applyLayout() {
    track("layout");
    const l = useApp.getState().layout;
    if (!l || l.mode !== "fill" || !this.store) return;
    const preset = PRESETS.find((p) => p.id === l.preset)!;
    const objs = this.fillObjects(l);
    if (!objs.length) return;
    const n = new Set(this.store.list().filter((o) => o.groupName?.startsWith(preset.name)).map((o) => o.group)).size + 1;
    this.createLayoutGroup(objs, `${preset.name} ${n}`);
    this.cancelLayout();
    useApp.getState().set({ layoutPanel: null });
  }

  private createLayoutGroup(objs: LayoutObject[], name: string) {
    const store = this.store!;
    const seated = objs.map((o) => ({ ...o, y: o.y ?? this.tools?.seatY(o, 64) ?? 64 }));
    const made = store.createGroup(seated, useApp.getState().dimension, name, useApp.getState().collab.name);
    select(made.map((o) => o.id));
    return made;
  }

  selectGroup(group: string) {
    if (!group) return;
    const ids = this.store?.groupMembers(group).map((o) => o.id) ?? [];
    select(ids);
  }

  // templates

  allTemplates(): { builtin: Template[]; world: Template[]; mine: Template[] } {
    const s = useApp.getState();
    return { builtin: BUILTIN_TEMPLATES, world: s.worldTemplates, mine: s.myTemplates };
  }

  private findTemplate(id: string) {
    const t = this.allTemplates();
    return [...t.builtin, ...t.world, ...t.mine].find((x) => x.id === id) ?? null;
  }

  startPlacing(templateId: string) {
    this.cancelSchematicPlacing();
    useApp.getState().set({ layoutPanel: "templates", layout: { mode: "place", templateId, turns: 0, scale: 1, mirror: false, scaleSizes: false }, tool: "select" });
  }

  private placedObjects(at: [number, number]): TemplateObject[] {
    const l = useApp.getState().layout;
    if (!l || l.mode !== "place") return [];
    const t = this.findTemplate(l.templateId);
    return t ? placeTemplate(t, { at, turns: l.turns, scale: l.scale, mirror: l.mirror, scaleSizes: l.scaleSizes }) : [];
  }

  private previewPlacement() {
    this.renderer?.setPreview(this.placeAt ? this.asPreview(this.placedObjects(this.placeAt)) : null);
  }

  private armPlacement() {
    if (!this.tools) return;
    this.tools.placement = {
      hover: (pt) => { this.placeAt = pt; this.previewPlacement(); },
      click: (pt, e) => {
        const l = useApp.getState().layout;
        if (!l || l.mode !== "place") return;
        const t = this.findTemplate(l.templateId);
        const objs = this.placedObjects(pt);
        if (!t || !objs.length) return;
        this.createLayoutGroup(objs, t.name);
        if (!e.shiftKey) { this.cancelLayout(); useApp.getState().set({ layoutPanel: null }); }
      },
      key: (e) => {
        const l = useApp.getState().layout;
        if (!l || l.mode !== "place" || e.ctrlKey || e.metaKey || e.altKey) return false;
        if (e.key === "Escape") { this.cancelLayout(); return true; }
        if (e.code === "KeyR") { this.updateLayout({ turns: (l.turns + (e.shiftKey ? 3 : 1)) % 4 }); return true; }
        if (e.code === "KeyF") { this.updateLayout({ mirror: !l.mirror }); return true; }
        if (e.code === "BracketRight" || e.key === "]") { this.updateLayout({ scale: Math.min(4, l.scale + 0.25) }); return true; }
        if (e.code === "BracketLeft" || e.key === "[") { this.updateLayout({ scale: Math.max(0.25, l.scale - 0.25) }); return true; }
        return false;
      },
    };
  }

  /** Save the selected objects as a template (world library, and optionally my own library). */
  saveTemplate(name: string, alsoMine: boolean) {
    const store = this.store;
    const objs = useApp.getState().selectedIds.map((id) => store?.get(id)).filter((o): o is PlanObject => !!o);
    if (!store || !objs.length) return null;
    const t = makeTemplate(`tpl-${newId()}`, name.trim() || "Template", objs.map((o) => ({
      kind: o.kind, name: o.name, points: buildingPoints(o), color: o.color,
      ...(o.kind === "path" ? { width: o.width } : {}), ...(o.kind === "building" ? { height: o.height, ...(o.storey ? { storey: o.storey } : {}) } : {}),
      ...(o.material ? { material: o.material } : {}), ...(o.notes ? { notes: o.notes } : {}),
      ...(o.sourceUrl ? { sourceUrl: o.sourceUrl } : {}), ...(o.schematic ? { schematic: o.schematic } : {}),
    })), useApp.getState().collab.name);
    store.saveTemplate(t);
    if (alsoMine) this.saveMyTemplates([t, ...useApp.getState().myTemplates.filter((x) => x.id !== t.id)]);
    return t;
  }

  deleteTemplate(id: string) {
    this.store?.deleteTemplate(id);
    this.saveMyTemplates(useApp.getState().myTemplates.filter((x) => x.id !== id));
  }

  private saveMyTemplates(list: Template[]) {
    useApp.getState().set({ myTemplates: list });
    try { localStorage.setItem("mcp-templates", JSON.stringify(list.slice(0, 100))); } catch { /* ignore */ }
  }

  // ---------------- schematics ----------------
  //
  // Imported files live only in this browser (lib/schematics.ts). A placed schematic is a normal
  // building whose `schematic` field names the file by hash; its blocks are drawn by StructureRenderer
  // for everyone who has the same file, and it keeps a link to where the file came from.

  /** Reload my schematic library from IndexedDB. */
  async refreshSchematics() {
    this.applySchematicList(await listSchematics());
  }

  private applySchematicList(list: SchematicEntry[]) {
    useApp.getState().set({ schematics: list });
    this.structures?.setAvailable(list.map((e) => e.hash));
    this.renderer?.rebuildAll();
  }

  schematicEntry(hash: string) {
    return useApp.getState().schematics.find((e) => e.hash === hash) ?? null;
  }

  /**
   * Read a schematic file and keep it in my library (this browser). Returns the entry (also when the
   * same file was imported before), or null with an error message shown.
   */
  async importSchematic(file: File, sourceUrl?: string): Promise<SchematicEntry | null> {
    const app = useApp.getState();
    if (!this.engine || app.phase !== "ready") { app.set({ error: "Open a world first, then import schematics." }); return null; }
    if (file.size > MAX_SCHEMATIC_BYTES) { app.set({ error: `“${file.name}” is too big (${Math.round(file.size / 1048576)} MB, max ${MAX_SCHEMATIC_BYTES / 1048576} MB).` }); return null; }
    app.set({ schemImporting: true, error: null });
    try {
      const hash = await sha256Hex(file);
      const have = this.schematicEntry(hash);
      if (have) {
        const link = normalizeSourceUrl(sourceUrl);
        if (link && !have.sourceUrl) { this.applySchematicList(await updateSchematic(hash, { sourceUrl: link })); return this.schematicEntry(hash); }
        return have;
      }
      const info = await this.engine.parseStructure(hash, file, file.name);
      if (!info.blocks) throw new Error("This schematic has no blocks in it.");
      const metaName = info.name && !/^(unnamed|untitled|schematic)$/i.test(info.name) ? info.name : null;
      const link = normalizeSourceUrl(sourceUrl);
      const entry: SchematicEntry = {
        hash, name: (metaName ?? nameFromFile(file.name)).slice(0, 80), fileName: file.name, format: info.format, version: info.version, dataVersion: info.dataVersion,
        size: info.size, blocks: info.blocks, materials: info.materials, bytes: file.size, addedAt: Date.now(),
        ...(info.author ? { author: info.author.slice(0, 100) } : {}),
        ...(info.description ? { description: info.description.slice(0, 2000) } : {}),
        ...(info.preview ? { preview: previewDataUrl(info.preview) } : {}),
        ...(link ? { sourceUrl: link } : {}),
      };
      this.applySchematicList(await saveSchematic(entry, file));
      return entry;
    } catch (e) {
      useApp.getState().set({ error: (e as Error).message });
      return null;
    } finally {
      useApp.getState().set({ schemImporting: false });
    }
  }

  /** Import in the Schematics view (or a file dropped on the map): then ask for its name / source link there. */
  async importAndEdit(file: File) {
    const e = await this.importSchematic(file);
    if (e) {
      if (useApp.getState().layoutPanel !== "schematics") this.openSchematics();
      useApp.getState().set({ schemSelected: e.hash, schemEdit: e.hash });
    }
    return e;
  }

  async editSchematic(hash: string, patch: { name?: string; sourceUrl?: string }) {
    const p: Partial<SchematicEntry> = {};
    if (patch.name !== undefined) p.name = patch.name.trim().slice(0, 80) || "Schematic";
    if (patch.sourceUrl !== undefined) p.sourceUrl = normalizeSourceUrl(patch.sourceUrl) ?? undefined;
    this.applySchematicList(await updateSchematic(hash, p));
  }

  async deleteSchematic(hash: string) {
    if (useApp.getState().schemPlace?.hash === hash) this.cancelSchematicPlacing();
    this.applySchematicList(await removeSchematic(hash));
    if (useApp.getState().schemEdit === hash) useApp.getState().set({ schemEdit: null });
  }

  // the full-window Schematics view (ui/schematics/): its 3D preview, thumbnails and material swatches

  /** The person's own block textures for the view's preview (null = map colours only: coloured cubes). */
  previewTextures(): AssetBundle["textures"] | null {
    return useApp.getState().mapColoursOnly ? null : this.bundle?.textures ?? null;
  }

  /** Average colour of a block's top texture (tinted like plains), null without textures or a model. */
  blockColour(id: string): [number, number, number] | null {
    const t = this.previewTextures();
    const m = t ? this.material(id) : null;
    if (!t || !m) return null;
    const tint = m.tintTop ? DEFAULT_TINT[m.tint] : undefined;
    const c: [number, number, number] = [t.avg[m.top * 3], t.avg[m.top * 3 + 1], t.avg[m.top * 3 + 2]];
    return tint === undefined ? c : [Math.round((c[0] * (tint >> 16)) / 255), Math.round((c[1] * ((tint >> 8) & 255)) / 255), Math.round((c[2] * (tint & 255)) / 255)];
  }

  private async schematicBlob(hash: string) {
    const blob = await getSchematicFile(hash);
    if (!blob || !this.engine) throw new Error("The file isn't on this computer any more.");
    return { blob, name: this.schematicEntry(hash)?.fileName ?? "schematic" };
  }

  /** Mesh a library schematic with the world's block models (lod > 1: a coarser copy, for very big builds). */
  async schematicMesh(hash: string, lod = 1) {
    const f = await this.schematicBlob(hash);
    return this.engine!.meshStructure(hash, f.blob, f.name, null, lod);
  }

  /** Outer blocks of a library schematic as cubes (no game textures), at most `max` (coarser otherwise). */
  async schematicVoxels(hash: string, lod = 1, max = 160_000) {
    const f = await this.schematicBlob(hash);
    return this.engine!.structureVoxels(hash, f.blob, f.name, lod, max);
  }

  /** Keep a rendered thumbnail with the library entry (in this browser only, like the file itself). */
  async setSchematicThumb(hash: string, thumb: string, thumbKey: string) {
    useApp.getState().set({ schematics: await updateSchematic(hash, { thumb, thumbKey }) });
  }

  /** The plan's reference to a library file in a given orientation. */
  schematicRef(e: SchematicEntry, turns = 0, mirror = false): SchematicRef {
    return {
      hash: e.hash, file: e.fileName, format: e.format, size: e.size, turns, mirror, blocks: e.blocks,
      ...(e.author ? { author: e.author } : {}),
      materials: e.materials.slice(0, 200),
    };
  }

  /** Schematics used in this world's plan whose file isn't in my library (a friend placed them). */
  missingSchematics(): { ref: SchematicRef; name: string; sourceUrl?: string; count: number }[] {
    const s = useApp.getState();
    const have = new Set(s.schematics.map((e) => e.hash));
    const out = new Map<string, { ref: SchematicRef; name: string; sourceUrl?: string; count: number }>();
    for (const o of s.plans) {
      if (!o.schematic || have.has(o.schematic.hash)) continue;
      const cur = out.get(o.schematic.hash);
      if (cur) { cur.count++; cur.sourceUrl ??= o.sourceUrl; }
      else out.set(o.schematic.hash, { ref: o.schematic, name: o.name, sourceUrl: o.sourceUrl, count: 1 });
    }
    return [...out.values()];
  }

  /**
   * Give a schematic building its file (e.g. downloaded from its source link). Same file → it shows up.
   * A different file is only used when `replace` is set (the building then takes its size).
   */
  async attachSchematicFile(objectId: string, file: File, replace = false): Promise<"ok" | "different" | "failed"> {
    const o = this.store?.get(objectId);
    if (!o?.schematic) return "failed";
    const e = await this.importSchematic(file, o.sourceUrl);
    if (!e) return "failed";
    if (e.hash === o.schematic.hash) return "ok";
    if (!replace) return "different";
    const ref = this.schematicRef(e, o.schematic.turns, o.schematic.mirror);
    const [x0, z0] = schematicOrigin(o.points);
    this.store!.update(o.id, { schematic: ref, points: schematicFootprint(ref, x0, z0), height: ref.size[1], ...(e.sourceUrl && !o.sourceUrl ? { sourceUrl: e.sourceUrl } : {}) });
    return "ok";
  }

  // placing

  private schemAt: [number, number] | null = null;

  /** Place a library schematic on the map (closes the Schematics view; turned / mirrored like its preview). */
  startSchematicPlacing(hash: string, orient: { turns?: number; mirror?: boolean } = {}) {
    if (!this.tools || !this.schematicEntry(hash)) return;
    this.cancelLayout();
    useApp.getState().set({
      layoutPanel: null, schemPlace: { hash, turns: ((orient.turns ?? 0) % 4 + 4) % 4, mirror: !!orient.mirror }, tool: "select", schemEdit: null, schemSelected: hash,
    });
    void updateSchematic(hash, { usedAt: Date.now() }).then((list) => useApp.getState().set({ schematics: list }));
    this.tools.placement = {
      hover: (pt) => { this.schemAt = pt; this.previewSchematic(); },
      click: (pt, e) => this.placeSchematic(pt, e.shiftKey),
      key: (e) => {
        const sp = useApp.getState().schemPlace;
        if (!sp || e.ctrlKey || e.metaKey || e.altKey) return false;
        if (e.key === "Escape") { this.cancelSchematicPlacing(); return true; }
        if (e.code === "KeyR") { this.updateSchematicPlacing({ turns: (sp.turns + (e.shiftKey ? 3 : 1)) % 4 }); return true; }
        if (e.code === "KeyF") { this.updateSchematicPlacing({ mirror: !sp.mirror }); return true; }
        return false;
      },
    };
    this.previewSchematic();
  }

  updateSchematicPlacing(patch: Partial<{ turns: number; mirror: boolean }>) {
    const sp = useApp.getState().schemPlace;
    if (!sp) return;
    useApp.getState().set({ schemPlace: { ...sp, ...patch } });
    this.previewSchematic();
  }

  cancelSchematicPlacing() {
    if (!useApp.getState().schemPlace) return;
    if (this.tools) this.tools.placement = null;
    this.structures?.setGhost(null);
    this.renderer?.setPreview(null);
    useApp.getState().set({ schemPlace: null, draftInfo: null });
  }

  /** where the schematic would go with the mouse at `at` */
  private schematicPlacement(at: [number, number]) {
    const sp = useApp.getState().schemPlace;
    const e = sp && this.schematicEntry(sp.hash);
    if (!sp || !e) return null;
    const ref = this.schematicRef(e, sp.turns, sp.mirror);
    const points = schematicFootprintAt(ref, at);
    const y = this.tools?.seatY({ kind: "building", points }, 64) ?? 64;
    return { e, ref, points, y };
  }

  private previewSchematic() {
    const p = this.schemAt ? this.schematicPlacement(this.schemAt) : null;
    if (!p) { this.structures?.setGhost(null); this.renderer?.setPreview(null); return; }
    const [x0, z0] = schematicOrigin(p.points);
    this.structures?.setGhost({ ref: p.ref, x0, y: p.y, z0 });
    const dim = useApp.getState().dimension;
    this.renderer?.setPreview([{
      id: "preview-schematic", kind: "building", name: p.e.name, color: "#ffffff", status: "idea", dimension: dim, points: p.points, y: p.y,
      width: 0, height: p.ref.size[1], notes: "", author: "", createdAt: 0, updatedAt: 0, schematic: p.ref,
    }]);
    // the placing card (ui/LayoutPanel.tsx) shows name, size, the keys and Turn / Mirror / Stop: no second hint
    if (useApp.getState().draftInfo) useApp.getState().set({ draftInfo: null });
  }

  private placeSchematic(at: [number, number], more: boolean) {
    const p = this.schematicPlacement(at);
    const store = this.store;
    if (!p || !store) return;
    track("schematic");
    store.undo.stopCapturing();
    const o = store.create({
      kind: "building", name: p.e.name, points: p.points, y: p.y, height: p.ref.size[1], dimension: useApp.getState().dimension,
      author: useApp.getState().collab.name, schematic: p.ref, ...(p.e.sourceUrl ? { sourceUrl: p.e.sourceUrl } : {}),
    });
    store.undo.stopCapturing();
    if (!more) { this.cancelSchematicPlacing(); select([o.id]); }
  }

  /** Material list as text (for copying into a note or a chat). */
  materialsText(o: PlanObject) {
    const lines = (o.schematic?.materials ?? []).map(([id, n]) => `${n.toLocaleString("en-US")} × ${this.itemName(id)}${n >= 64 ? ` (${Math.floor(n / 64)} stacks${n % 64 ? ` + ${n % 64}` : ""})` : ""}`);
    return `${o.name} — materials${o.sourceUrl ? `\nSource: ${o.sourceUrl}` : ""}\n${lines.join("\n")}`;
  }

  /** Block/item id → the id the loaded game knows (renamed blocks) */
  knownBlock(id: string) {
    const bs = this.bundle?.blockstates;
    return bs ? resolveBlockName(id, (n) => !!bs[n]) : id;
  }

  itemName(id: string) {
    const [ns, path] = id.includes(":") ? id.split(":", 2) : ["minecraft", id];
    const name = path.split("_").map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");
    return ns === "minecraft" ? name : `${name} (${ns})`;
  }

  // ---------------- pinboard ----------------

  /** where images of this room live on the server (the host's server for guests) */
  imageRemote() {
    const s = useApp.getState();
    const server = s.guestOf ?? s.collab.url;
    if (s.room?.kind === "account") {
      const id = s.room.id;
      return { server, room: id, headers: () => roomHeaders(server, id) };
    }
    return s.worldId && s.room?.kind === "legacy" ? { server, room: s.worldId } : null;
  }

  openBoard(objectId: string | null) {
    useApp.getState().set({ boardFor: objectId });
  }

  addCard(objectId: string, type: CardType, init: Partial<BoardCard> = {}) {
    const store = this.store;
    if (!store) return null;
    store.undo.stopCapturing();
    const c = store.addCard({ objectId, type, author: useApp.getState().collab.name, ...(type === "check" ? { items: [] } : {}), ...(type === "note" ? { text: "" } : {}), ...init });
    track("board");
    return c;
  }

  /** Add images (files / pasted blobs) to a board. */
  async addImages(objectId: string, files: Blob[], at?: { x: number; y: number }) {
    let i = 0;
    for (const f of files) {
      try {
        const image = await prepareImage(f, (f as File).name);
        const ratio = image.h / Math.max(1, image.w);
        const w = 260, h = Math.round(Math.min(420, Math.max(120, w * ratio)) + 34);
        this.addCard(objectId, "image", { image, w, h, ...(at ? { x: at.x + i * 24, y: at.y + i * 24 } : {}) });
        i++;
      } catch (e) {
        useApp.getState().set({ error: `Image not added: ${(e as Error).message}` });
      }
    }
  }

  /** Add a link card and fetch its preview (title, image) through the server. */
  async addLink(objectId: string, url: string, at?: { x: number; y: number }) {
    const c = this.addCard(objectId, "link", { link: { url }, ...(at ?? {}) });
    if (c) await this.refreshLink(c.id);
  }

  async refreshLink(cardId: string) {
    const store = this.store;
    const c = store?.getCard(cardId);
    if (!store || !c?.link) return;
    const s = useApp.getState();
    s.set({ unfurling: [...s.unfurling, cardId] });
    try {
      const p = await unfurl(s.guestOf ?? s.collab.url, c.link.url);
      store.updateCard(cardId, {
        link: {
          url: c.link.url,
          ...(p.title ? { title: p.title } : {}),
          ...(p.description ? { description: p.description } : {}),
          ...(p.site ? { site: p.site } : {}),
          ...(p.image ? { image: p.image } : {}),
          ...(p.blocked ? { blocked: true } : {}),
        },
      });
    } catch (e) {
      console.warn("link preview failed", e);
      store.updateCard(cardId, { link: { ...c.link, blocked: true } });
    } finally {
      useApp.getState().set({ unfurling: useApp.getState().unfurling.filter((x) => x !== cardId) });
    }
  }

  /** Board paste: image → image card, link → link card, other text → note. */
  async pasteIntoBoard(objectId: string, data: DataTransfer, at?: { x: number; y: number }) {
    const files = [...data.files].filter((f) => /^image\//.test(f.type));
    if (files.length) return this.addImages(objectId, files, at);
    const text = (data.getData("text/uri-list") || data.getData("text/plain")).trim();
    if (!text) return;
    if (looksLikeUrl(text.split(/\s+/)[0])) return this.addLink(objectId, text.split(/\s+/)[0], at);
    this.addCard(objectId, "note", { text: text.slice(0, 5000), ...(at ?? {}) });
  }

  private imageSyncTimer = 0;
  /** While live: upload images the server doesn't have yet (debounced). */
  scheduleImageSync(delay = 1500) {
    clearTimeout(this.imageSyncTimer);
    this.imageSyncTimer = window.setTimeout(async () => {
      const s = useApp.getState();
      const r = this.imageRemote();
      if (!r || s.collab.status !== "connected" || !this.store || s.readOnly) return;
      try { await uploadMissing(r, this.store.imageHashes()); }
      catch (e) { useApp.getState().set({ error: `Pinboard images couldn't be shared: ${(e as Error).message}` }); }
    }, delay);
  }

  /** 📌 badges (count + first image) on the map labels */
  private async refreshBadges() {
    const store = this.store;
    if (!store || !this.renderer) return;
    const sum = store.boardSummary();
    const remote = this.imageRemote();
    const out = new Map<string, { count: number; image?: string }>();
    await Promise.all([...sum].map(async ([id, v]) => {
      out.set(id, { count: v.count, image: v.image ? (await imageUrl(v.image, remote)) ?? undefined : undefined });
    }));
    this.renderer.setBoards(out);
  }

  // ---------------- day & night ----------------

  private timeFromEngine = false;

  setLighting(p: Partial<{ on: boolean; time: number; playing: boolean; speed: number }>) {
    const cur = useApp.getState().lighting;
    // picking a time or pressing play switches the lighting on
    const on = p.on ?? (p.time !== undefined || p.playing ? true : cur.on);
    useApp.getState().set({ lighting: { ...cur, ...p, on, playing: on ? (p.playing ?? cur.playing) : false } });
  }

  /** the world's own time of day (from level.dat / world_clocks.dat), if known */
  worldTime(): number | null {
    return useApp.getState().level?.dayTime ?? null;
  }

  revealPath(path: string) {
    void revealPath(path).catch((e) => useApp.getState().set({ error: String((e as Error)?.message ?? e) }));
  }

  saveVersion(name: string) {
    return this.session?.store.saveVersion(name, useApp.getState().collab.name);
  }

  restoreVersion(id: string) {
    select([]);
    return this.session?.store.restoreVersion(id, useApp.getState().collab.name) ?? false;
  }

  deleteVersion(id: string) {
    this.session?.store.deleteVersion(id);
  }

  undo() { this.session?.store.undo.undo(); }
  redo() { this.session?.store.undo.redo(); }

  // ---------------- collaboration ----------------

  connect(url: string, name: string) {
    if (!this.session) return;
    if (this.demo) {
      // everyone's demo island has the same room id: never mix strangers' demo plans
      useApp.getState().set({ error: "The demo island stays on this computer. Open your own world to plan together." });
      return;
    }
    const s = useApp.getState();
    const room = s.room;
    if (room?.kind === "account") name = this.identity(name);
    else this.session.setIdentity({ uid: "" });
    useApp.getState().set({ collab: { ...useApp.getState().collab, url, name } });
    if (room?.kind === "account") {
      // every (re)connect gets a fresh ticket: role changes and removals apply at once
      this.session.connect(url, name, { room: room.id, ticket: () => this.liveTicket(url, room.id) });
    } else if (room?.kind === "legacy") {
      // a world shared before accounts (open link until the cut-off)
      this.session.connect(url, name, { room: room.id, hostKey: s.guestOf ? null : hostKeyFor(url, room.id) });
    } else {
      // host going live: the world needs its shared room first (sign-in)
      void this.ensureRoom(() => this.connect(url, name)).then((r) => { if (r) this.connect(url, name); });
    }
  }

  /**
   * In worlds of accounts everyone is their account: one name + colour for the cursor, the name tag and the People
   * list. A name typed in the Share panel / the invite card becomes the account's name. Returns the name to use.
   */
  private identity(typed: string) {
    const a = useAccount.getState();
    if (a.status !== "signed-in" || !a.profile) return typed;
    const wanted = typed.trim().slice(0, 40);
    if (wanted && wanted !== a.profile.displayName) void updateProfile({ displayName: wanted }).catch(() => {});
    const name = wanted || a.profile.displayName;
    this.session?.setIdentity({ name, color: a.profile.color, uid: a.user?.id });
    // later changes (account menu, another tab) reach the others at once
    this.identitySub ??= useAccount.subscribe((now, before) => {
      if (!now.profile || now.profile === before.profile || useApp.getState().room?.kind !== "account") return;
      this.session?.setIdentity({ name: now.profile.displayName, color: now.profile.color, uid: now.user?.id });
      useApp.getState().set({ collab: { ...useApp.getState().collab, name: now.profile.displayName } });
    });
    return name;
  }
  private identitySub: (() => void) | null = null;

  /** "Your name" in the Share panel: the account's name in worlds of accounts, this browser's name otherwise */
  setMyName(name: string) {
    const n = name.trim().slice(0, 40);
    if (!n) return;
    if (useApp.getState().room?.kind === "account" && useAccount.getState().status === "signed-in") {
      this.identity(n);
      return;
    }
    useApp.getState().set({ collab: { ...useApp.getState().collab, name: n } });
    this.session?.setIdentity({ name: n });
  }

  /** Host: disconnect everyone and close the room until the host goes live again. */
  endSessionForEveryone() {
    this.session?.endSessionForEveryone();
  }

  /** may end the session for everyone: owner/admin of an account world */
  isHost() {
    const r = useApp.getState().room;
    return !!r && r.kind === "account" && !r.paused && (r.role === "owner" || r.role === "admin");
  }

  disconnect() {
    this.session?.disconnect();
  }
}

export const controller = new Controller();
